import { useParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useContrato } from '@/hooks/useContratos'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import {
    ArrowLeft, User, Car, FileText, MapPin, Phone, Mail,
    CreditCard, Receipt, Loader2, AlertTriangle, Calendar,
    Home, Gauge, Settings2, RefreshCw, ExternalLink, Trash2, CheckCircle, Layers
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { useState } from 'react'

export function ContratoDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const setPageTitle = useUIStore((s) => s.setPageTitle)

    const { data: contrato, isLoading, error, refetch } = useContrato(id!)
    const [isSyncing, setIsSyncing] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => { setPageTitle('Detalhe do Contrato') }, [setPageTitle])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 size={32} className="animate-spin text-primary-600" />
                    <p className="text-sm text-surface-700/60">Carregando contrato...</p>
                </div>
            </div>
        )
    }

    if (error || !contrato) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <AlertTriangle size={40} className="text-danger-400 mx-auto mb-3" />
                    <p className="text-surface-700">Contrato não encontrado</p>
                    <button onClick={() => navigate('/admin/contratos')} className="btn btn-secondary mt-4">
                        Voltar
                    </button>
                </div>
            </div>
        )
    }

    const c = contrato as any
    const assoc = c.associado || {}
    const veic = c.veiculo || {}

    return (
        <div className="space-y-6 animate-fade-in max-w-6xl">

            {/* ── Topo ── */}
            <div className="flex items-start gap-4">
                <button
                    onClick={() => navigate('/admin/contratos')}
                    className="p-2 rounded-xl hover:bg-surface-100 text-surface-700 transition-colors mt-0.5"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-xl font-bold text-surface-900 font-mono">
                            {c.external_reference || 'Contrato'}
                        </h2>
                        <span className={`badge ${getStatusBadgeClass(c.status)}`}>
                            {translateStatus(c.status)}
                        </span>
                        {c.plano && (
                            <span className="text-xs bg-primary-100 text-primary-700 font-bold px-2.5 py-1 rounded-full">
                                {c.plano}
                            </span>
                        )}
                        {(c.metadata as any)?.origem && (
                            <span className="text-[10px] bg-indigo-100 text-indigo-700 font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                                Canal: {(c.metadata as any)?.parceiro_nome || String((c.metadata as any).origem).replace('_', ' ')}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-surface-700/50 mt-0.5">
                        Aceite em {formatDate(c.data_inicio)} · Dia de vencimento: {c.dia_vencimento}
                    </p>
                </div>
                <div className="flex gap-2">
                    {c.metadata?.parceiro_id && (
                        <button 
                            onClick={async () => {
                                if (!window.confirm('Reenviar notificação de ativação para o parceiro?')) return
                                try {
                                    const resp = await fetch('https://xqlsvhezsjzrfuucpmou.supabase.co/functions/v1/api-resend-webhook', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ contrato_id: c.id })
                                    })
                                    if (resp.ok) alert('Webhook reenviado com sucesso!')
                                    else alert('Erro ao reenviar webhook.')
                                } catch (err) {
                                    alert('Falha na comunicação com o servidor.')
                                }
                            }}
                            className="btn btn-secondary gap-2"
                        >
                            <RefreshCw size={16} /> Reenviar Webhook
                        </button>
                    )}
                    {c.status !== 'ativo' && (
                        <button 
                            onClick={async () => {
                                setIsSyncing(true)
                                try {
                                    const { data, error } = await supabase.functions.invoke('asaas-sync-contrato', {
                                        body: { contrato_id: c.id }
                                    })
                                    if (error) throw error
                                    if (data?.activated) {
                                        alert('Sucesso! Pagamento identificado e contrato ativado.')
                                    } else {
                                        alert('Sincronização concluída. Situação atual: ' + data?.status)
                                    }
                                    refetch()
                                } catch (err: any) {
                                    alert('Erro ao sincronizar: ' + err.message)
                                } finally {
                                    setIsSyncing(false)
                                }
                            }}
                            disabled={isSyncing}
                            className={`btn ${isSyncing ? 'btn-secondary' : 'btn-primary'} gap-2 shadow-lg shadow-primary-500/20`}
                        >
                            {isSyncing ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />} 
                            Verificar Pagamento
                        </button>
                    )}
                    {c.status === 'ativo' && (
                        <button className="btn btn-primary gap-2">
                            <Receipt size={16} /> Gerar Fatura Avulsa
                        </button>
                    )}
                    <button 
                        onClick={async () => {
                            if (!window.confirm('Tem certeza que deseja excluir este contrato? Esta ação é reversível apenas pelo banco de dados.')) return
                            setIsDeleting(true)
                            try {
                                const { error } = await supabase
                                    .schema('veicular_system')
                                    .from('veic_contratos')
                                    .update({ deleted_at: new Date().toISOString() })
                                    .eq('id', c.id)
                                
                                if (error) throw error
                                alert('Contrato excluído com sucesso.')
                                navigate('/admin/contratos')
                            } catch (err: any) {
                                alert('Erro ao excluir: ' + err.message)
                            } finally {
                                setIsDeleting(false)
                            }
                        }}
                        disabled={isDeleting}
                        className="btn bg-danger-50 text-danger-600 hover:bg-danger-100 border-danger-100 gap-2"
                    >
                        {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} 
                        Excluir
                    </button>
                </div>
            </div>

            {/* ── Cards financeiros ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <FinCard label="Mensalidade" value={formatCurrency(c.valor_total_mensal)} highlight />
                <FinCard label="Adesão" value={formatCurrency(c.valor_adesao)} />
                <FinCard label="Taxa Admin" value={formatCurrency(c.valor_tx_admin)} />
                <FinCard label="Vistoria" value={formatCurrency(c.valor_vistoria)} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* ── Coluna Esquerda ── */}
                <div className="space-y-6">

                    {/* Dados do Associado */}
                    <Section title="Dados do Associado" icon={User}>
                        <Grid>
                            <Field label="Nome" value={assoc.nome || c.associado_nome} span={2} />
                            <Field label="CPF" value={assoc.cpf || c.associado_cpf} mono />
                            <Field label="Data de Nascimento" value={assoc.data_nascimento ? formatDate(assoc.data_nascimento) : undefined} />
                            <Field label="CNH" value={assoc.cnh} mono />
                            <Field label="Sexo" value={assoc.sexo} />
                            <Field label="Telefone" value={assoc.telefone || c.associado_telefone} icon={Phone} />
                            <Field label="Celular" value={assoc.celular} icon={Phone} />
                            <Field label="E-mail" value={assoc.email} icon={Mail} span={2} />
                        </Grid>
                        {assoc.logradouro && (
                            <div className="mt-3 pt-3 border-t border-surface-100">
                                <p className="text-[10px] font-bold text-primary-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <Home size={11} /> Endereço
                                </p>
                                <Grid>
                                    <Field label="Logradouro" value={assoc.logradouro} span={2} icon={MapPin} />
                                    <Field label="Número" value={assoc.numero} />
                                    <Field label="Complemento" value={assoc.complemento} />
                                    <Field label="Bairro" value={assoc.bairro} />
                                    <Field label="CEP" value={assoc.cep} mono />
                                    <Field label="Cidade" value={assoc.cidade} />
                                    <Field label="UF" value={assoc.uf} />
                                </Grid>
                            </div>
                        )}
                    </Section>

                    {/* Dados do Plano */}
                    <Section title="Dados do Plano" icon={FileText}>
                        <Grid>
                            <Field label="Plano" value={c.plano} />
                            <Field label="Cota / Participação" value={c.indice} />
                            <Field label="Valor Mensal" value={formatCurrency(c.valor_total_mensal)} highlight />
                            <Field label="Valor do Plano" value={formatCurrency(c.valor_nf)} />
                            <Field label="Taxa Administrativa" value={formatCurrency(c.valor_tx_admin)} />
                            <Field label="1ª Mensalidade" value={formatCurrency(c.valor_adesao)} />
                            <Field label="Vistoria" value={formatCurrency(c.valor_vistoria)} />
                            <Field label="Dia de Vencimento" value={c.dia_vencimento ? `Dia ${c.dia_vencimento}` : undefined} />
                            <Field label="Tipo de Pagamento" value={c.tipo_pagamento} />
                            <Field label="Ciclo" value={c.ciclo_faturamento} />
                            {c.desconto > 0 && <Field label="Desconto" value={formatCurrency(c.desconto)} />}
                            {c.justificativa && <Field label="Justificativa" value={c.justificativa} span={2} />}
                            {c.onde_desconto && <Field label="Onde desconto" value={c.onde_desconto} />}
                            {c.voluntario && <Field label="Voluntário" value={c.voluntario} />}
                        </Grid>
                        {c.obs && (
                            <div className="mt-3 pt-3 border-t border-surface-100">
                                <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-wider mb-1">Observações</p>
                                <p className="text-sm text-surface-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{c.obs}</p>
                            </div>
                        )}
                    </Section>

                    {/* Serviços Inclusos (Cross-sell/Vínculos) */}
                    {c.servicos && c.servicos.length > 0 && (
                        <Section title="Serviços Contratados" icon={Layers}>
                            <div className="space-y-2">
                                {c.servicos.map((link: any) => (
                                    <div key={link.id} className="flex justify-between items-center p-3 rounded-xl border border-surface-100 bg-surface-50/50">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${link.ativo ? 'bg-success-100 text-success-600' : 'bg-surface-200 text-surface-500'}`}>
                                                <Layers size={14} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-surface-900">{link.servico?.nome || 'Serviço Desconhecido'}</p>
                                                <p className="text-[10px] text-surface-500 font-medium">
                                                    {link.ativo ? 'Ativo' : 'Inativo'} · Incluído em {formatDate(link.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-primary-700">{formatCurrency(link.valor)}</p>
                                            {link.carencia_fim && (
                                                <p className="text-[10px] text-amber-600 font-medium">Carência até {formatDate(link.carencia_fim)}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                </div>

                {/* ── Coluna Direita ── */}
                <div className="space-y-6">

                    {/* Dados do Veículo */}
                    {(veic.placa || c.veiculo_placa) && (
                        <Section title="Dados do Veículo" icon={Car}>
                            <Grid>
                                <Field label="Placa" value={veic.placa || c.veiculo_placa} mono highlight />
                                <Field label="Fabricante" value={veic.marca || c.veiculo_marca} />
                                <Field label="Modelo" value={veic.modelo || c.veiculo_modelo} />
                                <Field label="Ano Fabricação" value={veic.ano_fabricacao} />
                                <Field label="Ano Modelo" value={veic.ano_modelo} />
                                <Field label="Cor" value={veic.cor} />
                                <Field label="Combustível" value={veic.combustivel} icon={Gauge} />
                                <Field label="Tipo de Veículo" value={veic.tipo_veiculo} />
                                <Field label="Espécie" value={veic.especie} />
                                <Field label="Qtd. Passageiros" value={veic.qtd_passageiros} />
                                <Field label="RENAVAM" value={veic.renavam} mono />
                                <Field label="Chassi" value={veic.chassi} mono />
                                <Field label="Nº Motor" value={veic.numero_motor} mono />
                                <Field label="UF Placa" value={veic.uf_placa} />
                                <Field label="Município Placa" value={veic.municipio_placa} />
                                <Field label="Código FIPE" value={veic.codigo_fipe} mono />
                                <Field label="Valor FIPE" value={veic.valor_fipe ? formatCurrency(veic.valor_fipe) : undefined} />
                                {veic.rastreador && <Field label="Rastreador" value={veic.rastreador ? 'Sim' : 'Não'} />}
                                {veic.valor_mes_rastreador > 0 && <Field label="Rastreador Mensal" value={formatCurrency(veic.valor_mes_rastreador)} />}
                            </Grid>
                        </Section>
                    )}

                    {/* Cobranças */}
                    <Section title="Faturas e Cobranças" icon={Receipt}>
                        {(!c.faturas || c.faturas.length === 0) ? (
                            <div className="rounded-xl border border-dashed border-surface-200 bg-surface-50 p-8 text-center">
                                <CreditCard size={36} className="text-surface-300 mx-auto mb-3" />
                                <p className="text-sm font-medium text-surface-700/60">Nenhuma fatura encontrada</p>
                                <p className="text-xs text-surface-700/40 mt-1">As cobranças sincronizadas do Asaas aparecerão aqui</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {c.faturas.sort((a: any, b: any) => new Date(b.data_vencimento).getTime() - new Date(a.data_vencimento).getTime()).map((f: any) => (
                                    <div key={f.id} className="p-3 rounded-xl border border-surface-100 bg-white hover:bg-surface-50 transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${f.status === 'paga' ? 'bg-success-100 text-success-600' : 'bg-amber-100 text-amber-600'}`}>
                                                    {f.status === 'paga' ? <Receipt size={16} /> : <Calendar size={16} />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-surface-900">
                                                        {formatCurrency(f.valor)}
                                                        <span className="ml-2 text-[10px] uppercase text-primary-600 font-black">
                                                            {f.tipo === 'mensalidade' ? 'Mensalidade' : f.tipo === 'adesao' ? 'Taxa de Adesão' : 'Avulsa'}
                                                        </span>
                                                    </p>
                                                    <p className="text-[10px] text-surface-500 font-medium leading-tight mt-0.5">
                                                        {f.metadata?.description || `Vence em ${formatDate(f.data_vencimento)}`}
                                                        {f.data_pagamento && ` · Pago em ${formatDate(f.data_pagamento)}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`badge text-[10px] uppercase font-bold ${
                                                    f.status === 'paga' ? 'bg-success-100 text-success-700' : 
                                                    f.status === 'atrasada' ? 'bg-danger-100 text-danger-700' : 
                                                    'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {f.status}
                                                </span>
                                                {(f.asaas_invoice_url || f.url_pix || f.metadata?.asaas_invoice_url || f.metadata?.invoiceUrl || f.url_boleto) && (
                                                    <a href={f.asaas_invoice_url || f.url_pix || f.metadata?.asaas_invoice_url || f.metadata?.invoiceUrl || f.url_boleto} target="_blank" rel="noreferrer" 
                                                       className="p-1.5 rounded-lg bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors border border-surface-200"
                                                       title="Ver Fatura no Asaas">
                                                        <ExternalLink size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>

                    {/* Metadados */}
                    <Section title="Sistema" icon={Settings2}>
                        <Grid>
                            <Field label="ID do Contrato" value={c.id} mono span={2} />
                            {c.asaas_subscription_id && <Field label="ID Asaas" value={c.asaas_subscription_id} mono span={2} />}
                            <Field label="Criado em" value={formatDate(c.created_at)} icon={Calendar} />
                            <Field label="Atualizado em" value={formatDate(c.updated_at)} icon={Calendar} />
                            {c.data_cancelamento && <Field label="Cancelado em" value={formatDate(c.data_cancelamento)} />}
                        </Grid>
                    </Section>

                </div>
            </div>
        </div>
    )
}

// ── Sub-componentes ────────────────────────────────────────────

function Section({ title, icon: Icon, children, badge }: {
    title: string; icon: any; children: React.ReactNode; badge?: React.ReactNode
}) {
    return (
        <div className="glass-card overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-surface-100 bg-surface-50/50">
                <div className="p-1.5 rounded-lg bg-primary-100 text-primary-600">
                    <Icon size={14} />
                </div>
                <p className="text-xs font-bold text-surface-700/60 uppercase tracking-widest">{title}</p>
                {badge}
            </div>
            <div className="p-5">{children}</div>
        </div>
    )
}

function Grid({ children }: { children: React.ReactNode }) {
    return <div className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>
}

function FinCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className={`glass-card p-4 text-center ${highlight ? 'border-primary-200 bg-primary-50' : ''}`}>
            <p className={`text-lg font-bold ${highlight ? 'text-primary-700' : 'text-surface-900'}`}>{value}</p>
            <p className="text-[10px] text-surface-700/50 uppercase font-bold mt-0.5">{label}</p>
        </div>
    )
}

function Field({ label, value, mono, highlight, span, icon: Icon }: {
    label: string; value?: string | number | null
    mono?: boolean; highlight?: boolean; span?: number; icon?: any
}) {
    if (value === null || value === undefined || value === '' || value === 0) return null
    return (
        <div className={span === 2 ? 'col-span-2' : ''}>
            <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-wider mb-0.5">{label}</p>
            <p className={`text-sm font-medium break-all leading-snug ${mono ? 'font-mono text-xs' : ''} ${highlight ? 'text-primary-600 font-bold' : 'text-surface-900'}`}>
                {Icon && <Icon size={11} className="inline mr-1 opacity-40" />}
                {String(value)}
            </p>
        </div>
    )
}
